import { Component, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  FormFieldConfig,
  ProviderClass,
  WidgetLayoutAttributes,
} from '@finxone-platform/shared/sys-config-types';
import { FinxoneSelectDropdownComponent } from '@finxone-platform/ui-components';
import { debounceTime, distinctUntilChanged, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { BeneficiaryFormService } from '../beneficiary-form.service';

@Component({
  selector: 'finxone-web-frontend-beneficiary-payment-rail-widget',
  templateUrl: './beneficiary-payment-rail-widget.component.html',
  imports: [ReactiveFormsModule, FinxoneSelectDropdownComponent],
})
export class BeneficiaryPaymentRailWidgetComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public inputFieldConfig: FormFieldConfig;
  public validationConfig: { type: string; message: string }[] = [];

  public beneficiaryForm: FormGroup;
  public inputLabel: string;
  public isPaymentRailSelectorVisible: boolean;

  constructor(
    public dataIn: ProviderClass<WidgetLayoutAttributes>,
    private beneficiaryFormService: BeneficiaryFormService,
    private accountService: AccountService,
  ) {}

  public ngOnInit(): void {
    this.initializeWidgetConfig();
  }

  private initializeWidgetConfig() {
    if (this.dataIn != undefined && this.dataIn.attri?.widgetProperties?.inputFieldConfig) {
      this.inputFieldConfig = this.dataIn.attri?.widgetProperties?.inputFieldConfig;
      this.inputLabel = this.dataIn.attri?.widgetProperties?.inputLabel || '';
      this.isPaymentRailSelectorVisible = this.dataIn.attri?.widgetProperties['isPaymentRailSelectorVisible'];
      this.beneficiaryForm = this.beneficiaryFormService.beneficiaryForm;
      this.validationConfig = this.beneficiaryFormService.getValidationConfig(
        this.inputFieldConfig.validation,
      );
      this.updatePaymentRailOptions();
    }
  }

  private updatePaymentRailOptions(): void {
    if (!this.beneficiaryForm) return;

    this.beneficiaryForm.valueChanges
      .pipe(
        // Emit the current form value immediately to load payment rail options on initialization
        startWith(this.beneficiaryForm.value),
        debounceTime(200),
        // Only emit if country or currency changed
        distinctUntilChanged(
          (prev, curr) => prev?.country === curr?.country && prev?.currency === curr?.currency,
        ),
        // Switch to the API call
        switchMap(({ country, currency }) => this.accountService.getPaymentSchemes(country, currency)),
        takeUntil(this.destroy$),
      )
      .subscribe((options) => {
        this.inputFieldConfig.options = options || [];
        const control = this.beneficiaryForm.controls[this.inputFieldConfig.name];

        // Check if the current value exists in the new options
        const isValid = options?.some((option) => option.value === control.value);

        // Set default value if current value is empty or invalid
        if (!isValid) {
          control.setValue(options?.[0]?.value ?? null, { emitEvent: false });
        }
      });
  }

  public get selectedOptionLabel(): string {
    const value = this.beneficiaryForm.controls[this.inputFieldConfig.name]?.value;
    const selected = this.inputFieldConfig.options?.find((option) => option?.value === value);
    return selected?.label ?? '';
  }
}
